import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getNewsBySection,
  deleteNews,
  createNews,
  updateNews,
} from "../../../../../services/newsServices";

import {
  filterNews,
  getFallbackSectionId,
  getNewsStats,
  isDemoMode,
  sortNewsByDate,
} from "../utils/actualitesUtils";

export default function useActualites(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    type: null,
    article: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchNews = useCallback(async () => {
    if (!finalSectionId) {
      setNews([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getNewsBySection(finalSectionId);
      const safeData = Array.isArray(data) ? data : [];

      setNews(sortNewsByDate(safeData));
    } catch (error) {
      console.error("Erreur lors de la récupération des actualités :", error);
      toast.error("Erreur lors de la récupération des actualités.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const stats = useMemo(() => getNewsStats(news), [news]);

  const rows = useMemo(() => {
    return filterNews(news, searchTerm, statusFilter);
  }, [news, searchTerm, statusFilter]);

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const openCreateDrawer = () => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "create",
      article: null,
    });
  };

  const openEditDrawer = (article) => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "edit",
      article,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      article: null,
    });
  };

  const handleCreate = async (payload) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      await createNews(payload);

      toast.success("Actualité créée avec succès.");
      await fetchNews();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'actualité :", error);
      toast.error("Erreur lors de la création de l'actualité.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (articleId, payload) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      await updateNews({
        id: articleId,
        ...payload,
      });

      toast.success("Actualité modifiée avec succès.");
      await fetchNews();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'actualité :", error);
      toast.error("Erreur lors de la mise à jour de l'actualité.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer cette actualité ?");

    if (!confirmed) return;

    try {
      await deleteNews(articleId);

      setNews((prev) => prev.filter((article) => article.id !== articleId));

      toast.success("Actualité supprimée avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'actualité :", error);
      toast.error("Erreur lors de la suppression de l'actualité.");
    }
  };

  return {
    finalSectionId,
    news,
    rows,
    stats,
    loading,
    saving,
    demoMode,

    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,

    drawer,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    fetchNews,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}